import React, { useState, useEffect, useCallback, useRef } from "react";  
import { useNavigate } from "react-router-dom";  
import api from "../api";  
import Navbar from "./Navbar";  
import { motion, AnimatePresence } from "framer-motion";  
  
const MyModels = () => {  
  const navigate = useNavigate();  
  const [assets, setAssets] = useState([]);  
  const [loading, setLoading] = useState(true);  
  const [togglingId, setTogglingId] = useState(null);  
  const [notification, setNotification] = useState(null);  
  
  const showNotification = useCallback((message, type = "info") => {  
    setNotification({ message, type });  
    setTimeout(() => setNotification(null), 4000);  
  }, []);  
  
  const fetchAssets = useCallback(async () => {  
    try {  
      const res = await api.get("/assets/available");  
      setAssets(res.data || []);  
    } catch (error) {  
      console.error("Failed to fetch assets:", error);  
      showNotification("Failed to load models", "error");  
    } finally {  
      setLoading(false);  
    }  
  }, [showNotification]);  
  
  useEffect(() => {  
    fetchAssets();  
    const interval = setInterval(fetchAssets, 30000);  
    return () => clearInterval(interval);  
  }, [fetchAssets]);  
  
  const handleToggle = async (asset) => {  
    if (!asset.has_credentials) {  
      showNotification(`Connect your ${asset.platform} account first`, "warning");  
      setTimeout(() => navigate("/accounts"), 1500);  
      return;  
    }  
  
    setTogglingId(asset.registry_id);  
    try {  
      const endpoint = asset.is_active  
        ? `/assets/${asset.registry_id}/deactivate`  
        : `/assets/${asset.registry_id}/activate`;  
      const res = await api.post(endpoint);  
      showNotification(res.data.msg, "success");  
      await fetchAssets();  
    } catch (error) {  
      console.error("Toggle failed:", error);  
      showNotification(error.response?.data?.detail || "Failed to update", "error");  
    } finally {  
      setTogglingId(null);  
    }  
  };  
  
  const activeCount = assets.filter((a) => a.is_active).length;  
  
  if (loading) {  
    return (  
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">  
        <Navbar />  
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">  
          <div className="text-center">  
            <div className="relative w-14 h-14 mx-auto mb-4">  
              <div className="absolute inset-0 border-4 border-gold/20 rounded-full" />  
              <div className="absolute inset-0 border-4 border-gold border-t-transparent rounded-full animate-spin" />  
            </div>  
            <p className="text-gold text-sm font-semibold">Loading your models…</p>  
          </div>  
        </div>  
      </div>  
    );  
  }  
  
  return (  
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black text-white">  
      <Navbar />  
  
      {/* Notification */}  
      <AnimatePresence>  
        {notification && (  
          <motion.div  
            initial={{ opacity: 0, y: -16 }}  
            animate={{ opacity: 1, y: 0 }}  
            exit={{ opacity: 0, y: -16 }}  
            className={`fixed top-20 left-4 right-4 sm:left-auto sm:right-6 z-50 px-5 py-3.5 rounded-xl shadow-2xl border backdrop-blur-md sm:max-w-md ${  
              notification.type === "success" ? "bg-green-900/90 border-green-500/60"  
              : notification.type === "warning" ? "bg-yellow-900/90 border-yellow-500/60"  
              : notification.type === "error" ? "bg-red-900/90 border-red-500/60"  
              : "bg-gray-900/90 border-gold/60"  
            }`}  
          >  
            <div className="flex items-center gap-3">  
              <div className={`w-2 h-2 rounded-full flex-shrink-0 animate-pulse ${  
                notification.type === "success" ? "bg-green-400"  
                : notification.type === "warning" ? "bg-yellow-400"  
                : notification.type === "error" ? "bg-red-400"  
                : "bg-gold"  
              }`} />  
              <p className="text-white text-sm font-medium">{notification.message}</p>  
            </div>  
          </motion.div>  
        )}  
      </AnimatePresence>  
  
      <div className="max-w-6xl mx-auto py-6 sm:py-10 px-4 sm:px-6 lg:px-8">  
        {/* Page Header */}  
        <motion.div  
          initial={{ opacity: 0, y: 12 }}  
          animate={{ opacity: 1, y: 0 }}  
          className="mb-8 sm:mb-10"  
        >  
          <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">  
            <div className="flex items-center gap-3">  
              <div className="w-1 h-10 bg-gradient-to-b from-gold to-yellow-600 rounded-full" />  
              <div>  
                <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">  
                  My Trading Models  
                </h1>  
                <p className="text-gray-400 text-sm mt-1">  
                  Switch AI-powered trading on or off for each market  
                </p>  
              </div>  
            </div>  
  
            {assets.length > 0 && (  
              <div className="flex items-center gap-2 px-4 py-2 bg-black/40 border border-gold/20 rounded-full backdrop-blur-sm">  
                <span className={`w-2 h-2 rounded-full ${activeCount > 0 ? "bg-green-400 animate-pulse" : "bg-gray-600"}`} />  
                <span className="text-xs font-semibold text-gray-300">  
                  {activeCount} of {assets.length} active  
                </span>  
              </div>  
            )}  
          </div>  
        </motion.div>  
  
        {assets.length === 0 ? (  
          <EmptyState />  
        ) : (  
          <>  
              <div  
                className={`  
                  grid gap-4 sm:gap-5 mb-8  
                  ${assets.length === 1  
                    ? "grid-cols-1 max-w-md"  
                    : assets.length === 2  
                    ? "grid-cols-1 sm:grid-cols-2 max-w-3xl"  
                    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}  
                `}  
              > 
              {assets.map((asset, index) => (  
                <motion.div  
                  key={asset.registry_id}  
                  initial={{ opacity: 0, y: 12 }}  
                  animate={{ opacity: 1, y: 0 }}  
                  transition={{ delay: index * 0.06 }}  
                >  
                  <ModelCard  
                    asset={asset}  
                    isToggling={togglingId === asset.registry_id}  
                    onToggle={() => handleToggle(asset)}  
                    onConnect={() => navigate("/accounts")}  
                  />  
                </motion.div>  
              ))}  
            </div>  
  
            {/* Help footer */}  
            <motion.div  
              initial={{ opacity: 0 }}  
              animate={{ opacity: 1 }}  
              transition={{ delay: 0.3 }}  
              className="bg-black/30 border border-gray-800 rounded-xl p-5 backdrop-blur-sm"  
            >  
              <div className="flex items-start gap-3">  
                <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center justify-center flex-shrink-0">  
                  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">  
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />  
                  </svg>  
                </div>  
                <div className="flex-1">  
                  <h4 className="text-sm font-semibold text-white mb-1">How this works</h4>  
                  <p className="text-xs text-gray-400 leading-relaxed">  
                    Each model uses AI to trade automatically based on live market signals. Turn one on and it starts working — entries, exits and risk are handled for you. You can run several models at once, and stop them anytime.  
                  </p>  
                </div>  
              </div>  
            </motion.div>  
          </>  
        )}  
      </div>  
    </div>  
  );  
};  
  
/* ─── Asset styling ─── */  
const ASSET_STYLES = {  
  BTC: { accent: "text-orange-400", iconBg: "bg-orange-500/10 border-orange-500/30" },  
  GOLD: { accent: "text-yellow-400", iconBg: "bg-yellow-500/10 border-yellow-500/30" },  
  FOREX: { accent: "text-green-400", iconBg: "bg-green-500/10 border-green-500/30" },  
};  
const DEFAULT_STYLE = { accent: "text-gray-400", iconBg: "bg-gray-500/10 border-gray-500/30" };  
  
/* ─── Asset icons ─── */  
const AssetIcon = ({ type, className = "" }) => {  
  const icons = {  
    BTC: (  
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">  
        <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />  
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />  
      </svg>  
    ),  
    GOLD: (  
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">  
        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />  
      </svg>  
    ),  
    FOREX: (  
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">  
        <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4z" />  
        <path fillRule="evenodd" d="M6 10a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />  
      </svg>  
    ),  
    DEFAULT: (  
      <svg className={className} fill="currentColor" viewBox="0 0 20 20">  
        <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm3 3a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm0 4a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1zm0 4a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />  
      </svg>  
    ),  
  };  
  return icons[type] || icons.DEFAULT;  
};  
  
/* ─── Bot Logs Hook ─── */  
const useBotLogs = (assetType, isActive, maxEntries = 30) => {  
  const [logs, setLogs] = useState([]);  
  const [lastTs, setLastTs] = useState(null);  
  
  useEffect(() => {  
    if (!isActive) return;  
  
    let cancelled = false;  
  
    const poll = async () => {  
      try {  
        const params = { asset_type: assetType, limit: 50 };  
        if (lastTs) params.since = lastTs;  
        
        const res = await api.get("/logs/bot_logs", { params });  
        if (cancelled) return;  
  
        const fresh = res.data?.logs || [];  
        if (fresh.length > 0) {  
          setLogs((prev) => {  
            const merged = [...prev, ...fresh];  
            return merged.slice(-maxEntries);  
          });  
          setLastTs(fresh[fresh.length - 1].ts);  
        }  
      } catch (e) {  
        // Silent — logs are non-critical  
      }  
    };  
  
    poll(); // immediate  
    const interval = setInterval(poll, 5000);  
    return () => {  
      cancelled = true;  
      clearInterval(interval);  
    };  
  }, [assetType, isActive, lastTs, maxEntries]);  
  
  return logs;  
};  
  
/* ─── Level styling ─── */  
const LEVEL_STYLES = {  
  TRADE: { dot: "bg-green-400", text: "text-green-300" },  
  WARN:  { dot: "bg-amber-400", text: "text-amber-300" },  
  ERROR: { dot: "bg-red-400",   text: "text-red-300"   },  
  INFO:  { dot: "bg-gray-500",  text: "text-gray-400"  },  
};  
  
/* ─── Logs Panel (per card) ─── */  
const LogsPanel = ({ assetType, isActive }) => {  
  const [expanded, setExpanded] = useState(false);  
  const logs = useBotLogs(assetType, isActive);  
  const scrollRef = useRef(null);
  
  // Auto-scroll to newest when expanded  
  useEffect(() => {  
    if (expanded && scrollRef.current) {  
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;  
    }  
  }, [logs, expanded]);  
  
  if (!isActive) return null;  
  
  const collapsedEntry = [...logs].reverse().find(e => e.level !== "INFO");  
  const visible = expanded ? logs : (collapsedEntry ? [collapsedEntry] : logs.slice(-1));  
  
  const formatTime = (iso) => {  
    try {  
      const d = new Date(iso);  
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });  
    } catch { return ""; }  
  };  
  
  return (  
    <div className="mt-3 pt-3 border-t border-gray-800/60">  
      {/* Header row — click to expand */}  
      <button  
        onClick={() => setExpanded((v) => !v)}  
        className="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 transition-colors mb-2"  
      >  
        <span className="flex items-center gap-1.5">  
          <span className="w-1 h-1 bg-gray-600 rounded-full animate-pulse" />  
          Bot Activity  
        </span>  
        <span className="flex items-center gap-1">  
          {logs.length > 0 && (  
            <span className="text-gray-600 normal-case tracking-normal">  
              {logs.length}  
            </span>  
          )}  
          <svg  
            className={`w-3 h-3 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}  
            fill="none" stroke="currentColor" viewBox="0 0 24 24"  
          >  
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />  
          </svg>  
        </span>  
      </button>  
  
      {/* Log entries */}  
      {logs.length === 0 ? (  
        <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-gray-600 italic">  
          <span className="w-1 h-1 bg-gray-700 rounded-full" />  
          Waiting for activity…  
        </div>  
      ) : (  
        <div  
          ref={scrollRef}  
          className={`  
            space-y-1 overflow-y-auto pr-1 transition-all duration-300  
            ${expanded ? "max-h-40" : "max-h-6"}  
            scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent  
          `}  
        >  
          <AnimatePresence initial={false}>  
            {visible.map((entry, idx) => {  
              const style = LEVEL_STYLES[entry.level] || LEVEL_STYLES.INFO;  
              return (  
                <motion.div  
                  key={`${entry.ts}-${idx}`}  
                  initial={{ opacity: 0, x: -4 }}  
                  animate={{ opacity: 1, x: 0 }}  
                  exit={{ opacity: 0 }}  
                  transition={{ duration: 0.15 }}  
                  className="flex items-start gap-2 text-[11px] leading-tight"  
                >  
                  <span className={`w-1 h-1 rounded-full flex-shrink-0 mt-1.5 ${style.dot}`} />  
                  {expanded && (  
                    <span className="text-gray-600 font-mono flex-shrink-0 mt-0.5">  
                      {formatTime(entry.ts)}  
                    </span>  
                  )}  
                  <span className={`flex-1 truncate ${style.text}`}>  
                    {entry.message}  
                  </span>  
                </motion.div>  
              );  
            })}  
          </AnimatePresence>  
        </div>  
      )}  
    </div>  
  );  
};  

/* ─── Model Card ─── */  
const ModelCard = ({ asset, isToggling, onToggle, onConnect }) => {  
  const style = ASSET_STYLES[asset.asset_type] || DEFAULT_STYLE;  
  const isActive = asset.is_active;  
  const canToggle = asset.has_credentials;  
  
  return (  
    <div  
      className={`  
        relative h-full flex flex-col p-5  
        bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60  
        backdrop-blur-xl rounded-2xl border transition-all duration-300  
        ${isActive  
          ? "border-green-500/40 shadow-lg shadow-green-500/10"  
          : "border-gray-800 hover:border-gold/30"}  
      `}  
    >  
      {/* Header */}  
      <div className="flex items-start justify-between mb-5">  
        <div className="flex items-center gap-3 min-w-0">  
          <div className={`w-11 h-11 border rounded-xl flex items-center justify-center flex-shrink-0 ${style.iconBg} ${style.accent}`}>  
            <AssetIcon type={asset.asset_type} className="w-5 h-5" />  
          </div>  
          <div className="min-w-0">  
            <h3 className="text-base font-bold text-white truncate leading-tight">  
              {asset.display_name}  
            </h3>  
            <p className="text-xs text-gray-500 font-mono truncate mt-0.5">  
              {asset.symbol}  
            </p>  
          </div>  
        </div>  
  
        {isActive && (  
          <span className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 border border-green-500/40 rounded-full flex-shrink-0">  
            <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />  
            <span className="text-[10px] font-bold uppercase tracking-wider text-green-400">Live</span>  
          </span>  
        )}  
      </div>  
  
      {/* Meta chips */}  
      <div className="flex flex-wrap gap-1.5 mb-5">  
        <MetaChip label={asset.platform} />  
        <MetaChip label={asset.timeframe} />  
      </div>  
  
      <div className="flex-1" />  
  
      {/* Action */}  
      {canToggle ? (  
        <button  
          onClick={onToggle}  
          disabled={isToggling}  
          className={`  
            w-full flex items-center justify-between px-4 py-3 rounded-xl  
            font-semibold text-sm transition-all duration-300  
            disabled:opacity-60 disabled:cursor-wait  
            ${isActive  
              ? "bg-green-500/5 border border-green-500/30 text-white hover:bg-red-500/5 hover:border-red-500/40"  
              : "bg-white/[0.03] border border-gray-800 text-gray-300 hover:bg-gold/5 hover:border-gold/40 hover:text-gold"}  
          `}  
        >  
          <span className="flex items-center gap-2">  
            {isToggling ? (  
              <>  
                <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />  
                <span>{isActive ? "Stopping…" : "Starting…"}</span>  
              </>  
            ) : (  
              <span>{isActive ? "Stop bot" : "Start bot"}</span>  
            )}  
          </span>  
          <Toggle on={isActive} />  
        </button>  
      ) : (  
        <button  
          onClick={onConnect}  
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-yellow-500/5 border border-yellow-500/40 text-yellow-400 font-semibold text-sm transition-all hover:bg-yellow-500/10 hover:border-yellow-500/60"  
        >  
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">  
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />  
          </svg>  
          Connect {asset.platform} to enable  
        </button>  
      )}  
      <LogsPanel assetType={asset.asset_type} isActive={isActive} />
    </div>  
  );  
};  
  
/* ─── Toggle switch ─── */  
const Toggle = ({ on }) => (  
  <span className={`relative inline-block w-9 h-5 rounded-full transition-colors duration-300 ${on ? "bg-green-500" : "bg-gray-700"}`}>  
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 ${on ? "translate-x-4" : "translate-x-0"}`} />  
  </span>  
);  
  
/* ─── Meta chip ─── */  
const MetaChip = ({ label }) => (  
  <span className="px-2.5 py-1 bg-black/40 border border-gray-800 rounded-md text-[11px] font-medium text-gray-400">  
    {label}  
  </span>  
);  
  
/* ─── Empty state ─── */  
const EmptyState = () => (  
  <motion.div  
    initial={{ opacity: 0, y: 12 }}  
    animate={{ opacity: 1, y: 0 }}  
    className="bg-gradient-to-br from-gray-900/50 via-black/50 to-gray-900/50 backdrop-blur-xl rounded-2xl border border-gray-800 p-12 text-center"  
  >  
    <div className="w-16 h-16 mx-auto mb-4 bg-gray-800/50 border border-gray-700 rounded-full flex items-center justify-center">  
      <svg className="w-7 h-7 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">  
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />  
      </svg>  
    </div>  
    <h3 className="text-lg font-semibold text-gray-200 mb-2">No models available yet</h3>  
    <p className="text-sm text-gray-500 max-w-sm mx-auto">  
      Trading models will appear here once your admin configures them.  
    </p>  
  </motion.div>  
);  
  
export default MyModels;  